import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import type { CartItemGiftPayload } from '@sola/schema/dotcom/cart/gift.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';

export interface AddGiftArgs {
  commitment: GraphQLCommitment;
  giftType: GiftTypeEnum;
  frequency: CommitmentFrequencyEnum;
  amount?: number;
  centerId?: string | null;
}

const giftTypesThatRequire = {
  globalId: new Set([
    GiftTypeEnum.Birthday,
    GiftTypeEnum.Child,
    GiftTypeEnum.Family,
    GiftTypeEnum.Final,
  ]),
  // TODO: Final gifts may require commitmentId
  commitmentId: new Set([
    GiftTypeEnum.Child,
    GiftTypeEnum.Family,
    GiftTypeEnum.Center,
  ]),
};

export const buildGiftCartItemPayload = ({
  commitment,
  giftType,
  frequency,
  amount,
  centerId,
}: AddGiftArgs): CartItemGiftPayload => {
  const commitmentId = commitment.internalId;
  const globalId = commitment.participant?.globalId ?? commitment.globalId;
  // interventionId would never be a centerId for a sponsorship commitment (it'll never be populated) - but we *could* fall back on parsing the localId if we had it
  const resolvedCenterId = centerId ?? undefined;

  if (!amount) {
    throw new Error(
      `Cannot build ${giftType} gift cart payload without amount`,
    );
  }

  if (!globalId && giftTypesThatRequire.globalId.has(giftType)) {
    throw new Error(
      `Cannot build ${giftType} gift cart payload without globalId`,
    );
  }
  if (!commitmentId && giftTypesThatRequire.commitmentId.has(giftType)) {
    throw new Error(
      `Cannot build ${giftType} gift cart payload without commitmentId`,
    );
  }

  const baseGiftItem = {
    itemType: CartItemTypeEnum.Gift,
    commitmentFrequency: frequency,
    unitPrice: amount,
  } satisfies Omit<CartItemGiftPayload, 'gift'>;

  switch (giftType) {
    case GiftTypeEnum.Birthday:
    case GiftTypeEnum.Final: {
      return {
        ...baseGiftItem,
        gift: {
          giftType,
          globalId,
          ...(commitmentId ? { commitmentId } : {}),
        },
      };
    }
    case GiftTypeEnum.Child:
    case GiftTypeEnum.Family: {
      return {
        ...baseGiftItem,
        gift: {
          giftType,
          commitmentId,
          globalId,
        },
      };
    }
    case GiftTypeEnum.Center: {
      if (!resolvedCenterId) {
        throw new Error(
          `Cannot build ${giftType} gift cart payload without centerId`,
        );
      }

      return {
        ...baseGiftItem,
        gift: {
          giftType,
          centerId: resolvedCenterId,
          commitmentId,
        },
      };
    }
    default: {
      const exhaustive: never = giftType;
      throw new Error(`Unhandled gift type: ${exhaustive}`);
    }
  }
};
